import React, { useState, useEffect, useCallback, useContext } from 'react';
import Evento from './Evento';
import Etiquetas from './Etiquetas';
import Codes from './Codigos';
import EventoProveedores from './EventoProveedores';
import EscaneosEvento from './EscaneosEvento';
import EstadisticasEvento from './EstadisticasEvento';
import { AuthContext } from '../contexts/AuthContext';
import { apiFetch } from '../utils/apiFetch';
import { useParams, useNavigate, Link } from 'react-router-dom';
import FilterModal from '../modal/FilterEventoModal';

interface Event 
{
  id: number;
  name: string;
  autonext: boolean;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  codereadr_service_id: string | null;
  codereadr_database_id: string | null;
}

interface Provider
{
  id: number;
  name: string;
}

const Eventos: React.FC = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const clientIdNum = Number(clientId);
  const { token } = useContext(AuthContext);
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [viewingScans, setViewingScans] = useState<number | null>(null); // Estado para gestionar la vista de escaneos
  const [viewingEstadisticas, setViewingEstadisticas] = useState<number | null>(null); // Estado para gestionar la vista de estadisticas
  const [title, setClientName] = useState<string>('');
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDir,   setSortDir]   = useState<'asc' | 'desc'>('asc');
  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false); 
  const [filters, setFilters] = useState({
    name: null as string | null,
    id_provider: null as number | null,
    start_dt: null as string | null,
    end_dt: null as string | null,
  });
  const [providers, setProviders] = useState<Provider[]>([]);

  //-----------------------------------------------------------

  const fetchEvents = useCallback(async () => 
  {
    setLoading(true);
    try 
    {
      const queryParams = new URLSearchParams({
                      id_client: Number(clientId).toString(),
                      ...(filters.name !== null && { name: filters.name }),
                      ...(filters.id_provider !== null && { id_provider: filters.id_provider.toString() }),
                      ...(filters.start_dt && { start_dt: filters.start_dt }),
                      ...(filters.end_dt && { end_dt: filters.end_dt }),
                  });

      const response = await apiFetch(`web/get_client_events?${queryParams.toString()}`, {
        method: 'GET'});

      if (response.ok) 
      {
        const data = await response.json();
        setEvents(data.events);
        setClientName(data.client_name)
      } 
      else 
      {
        navigate('/main/not-found'); 
      }
    } 
    catch (error) 
    {
      const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
      console.log('Error capturado:', errorMsg);  // ← añadir
      setError(errorMsg);
    } 
    finally 
    {
      setLoading(false);
    }
  }, [clientId, token, filters]);

  //-----------------------------------------------------------
  
  const fetchProviders = useCallback(async () =>
  {
      try
      {
          const response = await apiFetch(`web/get_providers`, {
              method: 'GET'
          });

          if (response.ok)
          {
              const data = await response.json();
              setProviders(data);
          }
          else
          {
              throw new Error('Error al cargar providers.');
          }
      }
          catch (error)
          {
            const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
            setError(errorMsg);
          }
      }, [token]);

  //-----------------------------------------------------------

  useEffect(() => 
  {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => 
  {
    fetchProviders();
  }, [fetchProviders]);

  //-----------------------------------------------------------

  const handleCreateClick = () => 
  {
    setSelectedEvent({
        id: -1, // Para indicar que es un nuevo evento
        name: '',
        autonext: false,
        start_date: '',
        end_date: '',
        start_time: '',
        end_time: '',
        codereadr_service_id: '',
        codereadr_database_id: ''}); // Crear un evento vacío para la creación
  };

  //-----------------------------------------------------------

  const handleEditClick = (event: Event) => 
  {
    setSelectedEvent(event); // Para editar el evento existente
  };

  //-----------------------------------------------------------

  const handleFormClose = () => 
  {
    setSelectedEvent(null); // Cerrar el formulario
    fetchEvents(); // Refrescar la lista de eventos después de guardar o eliminar
  };

  //-----------------------------------------------------------

  const handleFilterOpen = () => { setIsFilterModalOpen(true); };

  const handleApplyFilters = (filters: {
      name: string | null;
      id_provider: number | null;
      start_dt: string | null;
      end_dt: string | null;
  }) =>
  {
    setFilters(filters);
  };

  const handleClearFilters = () =>
  {
    setFilters({
        name: null,
        id_provider: null,
        start_dt: null,
        end_dt: null
    });
  };

  const i_Find_event_name_by_id = (id: number) => 
  {
        const event = events.find(event => event.id === id);
        return event ? event.name : '';
  };

  //-----------------------------------------------------------

  return (
    <div className="eventos-container">
      {!error && <div className="miguitas">Eventos de {title}</div>}

      {error ? (
        // Vista de error
        <div>
          <p className="error-message">{error}</p>
          <button onClick={() => navigate('/main/recintos')} className="create-client-button">
            <i className="fas fa-chevron-left"></i> Volver a Recintos
          </button>
        </div>

      ) : selectedEvent === null ? (
        // Vista normal — lista de eventos
        <div>
          <div className="cod-toolbar">
            <button className="icon-btn" onClick={() => navigate('/main/recintos')}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 19l-7-7 7-7"/>
              </svg>
              Volver a Recintos
            </button>
            <div className="cod-toolbar__right">
              <button className="icon-btn" onClick={handleFilterOpen}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M3 6h18M7 12h10M11 18h2"/></svg>
                Filtro
              </button>
              <button className="icon-btn icon-btn--create" onClick={handleCreateClick}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14"/>
                </svg>
                Crear evento
              </button>
            </div>
          </div>

          {loading && <p>Buscando eventos...</p>}
          {!loading && events.length === 0 && <p>No hay eventos para mostrar.</p>}

          {!loading && events.length > 0 && (() => {

            /* ── Ordenación ──────────────────────────────────────── */
            const sorted = [...events].sort((a, b) => {
              if (!sortField) return 0;
              let valA: string, valB: string;
              if (sortField === 'name') {
                valA = a.name.toLowerCase();
                valB = b.name.toLowerCase();
              } else if (sortField === 'date') {
                valA = a.start_date;
                valB = b.start_date;
              } else {
                valA = a.start_time;
                valB = b.start_time;
              }
              if (valA < valB) return sortDir === 'asc' ? -1 :  1;
              if (valA > valB) return sortDir === 'asc' ?  1 : -1;
              return 0;
            });

            const handleSort = (field: string) => {
              if (sortField === field) {
                setSortDir(d => d === 'asc' ? 'desc' : 'asc');
              } else {
                setSortField(field);
                setSortDir('asc');
              }
            };

            const sortOptions = [
              { value: 'name', label: 'Nombre' },
              { value: 'date', label: 'Fecha'  },
              { value: 'time', label: 'Hora'   },
            ];

            return (
              <>
                {/* ── Barra de ordenación ─────────────────────────── */}
                <div className="sort-bar">
                  <span className="sort-bar__label">Ordenar por</span>
                  <div className="sort-bar__options">
                    {sortOptions.map(opt => (
                      <button
                        key={opt.value}
                        className={`sort-bar__btn ${sortField === opt.value ? 'sort-bar__btn--active' : ''}`}
                        onClick={() => handleSort(opt.value)}
                      >
                        {opt.label}
                        {sortField === opt.value && (
                          <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11">
                            {sortDir === 'asc'
                              ? <path d="M8 12V4M5 7l3-3 3 3"/>
                              : <path d="M8 4v8M5 9l3 3 3-3"/>}
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ══ DESKTOP: tabla ══ */}
                <div className="table-scroll desktop-only">
                  <table className="eventos-table">
                    <tbody>
                      {sorted.map(event => {
                        const startStr = new Date(event.start_date).toLocaleDateString();
                        const endStr   = new Date(event.end_date).toLocaleDateString();
                        const dateStr  = startStr === endStr ? startStr : `${startStr} → ${endStr}`;
                        return (
                          <tr key={event.id}>

                            <td>
                              <div className='ev-main-cell'>
                              <span className="ev-name">{event.name}</span>
                              <span className={`ev-pill ${event.autonext ? 'ev-pill--green' : 'ev-pill--gray'}`}>
                                {event.autonext ? 'Autonext' : 'Normal'}
                              </span>
                              </div>
                            </td>

                            <td className="ev-date-cell">
                              {dateStr}
                            </td>

                            <td className="ev-time-cell">
                              {event.start_time} - {event.end_time}
                            </td>

                            <td className="evento-actions">
                              <div className="icon-btn-group">

                                <button className="icon-btn icon-btn--edit" onClick={() => handleEditClick(event)}>
                                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  Editar
                                </button>

                                <Link to={`/main/recintos/${clientId}/eventos/${event.id}/tags`} className="icon-btn icon-btn--tags" style={{ textDecoration: 'none' }}>
                                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                                    <path d="M9 2H2v7l7 7 7-7-7-7z"/>
                                    <circle cx="5" cy="5" r="1" fill="currentColor" stroke="none"/>
                                  </svg>
                                  Tags
                                </Link>

                                <Link to={`/main/recintos/${clientId}/eventos/${event.id}/providers`} className="icon-btn icon-btn--providers" style={{ textDecoration: 'none' }}>
                                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                                    <path d="M2 6l6-4 6 4v8H2V6z"/>
                                    <path d="M6 14v-4h4v4"/>
                                  </svg>
                                  Proveedores
                                </Link>
                                
                                <Link to={`/main/recintos/${clientId}/eventos/${event.id}/codes`} className="icon-btn icon-btn--codes" style={{ textDecoration: 'none' }}>
                                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                                    <rect x="2"    y="4" width="3"   height="8" rx="0.5"/>
                                    <rect x="7"    y="4" width="1.5" height="8" rx="0.5"/>
                                    <rect x="10.5" y="4" width="3"   height="8" rx="0.5"/>
                                  </svg>
                                  Códigos
                                </Link>

                                <Link to={`/main/recintos/${clientId}/eventos/${event.id}/scans`} className="icon-btn icon-btn--scans" style={{ textDecoration: 'none' }}>
                                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                                    <path d="M2 5V3h2M14 5V3h-2M2 11v2h2M14 11v2h-2M1 8h14"/>
                                  </svg>
                                  Escaneos
                                </Link>

                                <Link to={`/main/recintos/${clientId}/eventos/${event.id}/statistics`} className="icon-btn icon-btn--stats" style={{ textDecoration: 'none' }}>
                                  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                                    <path d="M2 13l4-5 3 3 4-7"/>
                                  </svg>
                                  Estadísticas
                                </Link>

                              </div>
                            </td>

                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* ══ MOBILE: cards ══ */}
                <div className="mobile-only event-cards">
                  {sorted.map(event => {
                    const startStr = new Date(event.start_date).toLocaleDateString();
                    const endStr   = new Date(event.end_date).toLocaleDateString();
                    const dateStr  = startStr === endStr ? startStr : `${startStr} → ${endStr}`;
                    return (
                      <div key={event.id} className="event-card">

                        <div className="event-card__header">
                          <span className="event-card__name">{event.name}</span>
                          <span className={`ev-pill ${event.autonext ? 'ev-pill--green' : 'ev-pill--gray'}`}>
                            {event.autonext ? 'Autonext' : 'Normal'}
                          </span>
                        </div>

                        <div className="event-card__meta">
                          <span>{dateStr}</span>
                          <span className="event-card__sep">·</span>
                          <span>{event.start_time} - {event.end_time}</span>
                        </div>

                        <div className="event-card__actions">

                          <button className="card-action-btn card-action-btn--edit" onClick={() => handleEditClick(event)}>
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                              <path d="M11 2l3 3L5 14H2v-3L11 2z"/>
                            </svg>
                            Editar
                          </button>

                          <Link to={`/main/recintos/${clientId}/eventos/${event.id}/tags`} className="card-action-btn" style={{ textDecoration: 'none' }}>
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                              <path d="M9 2H2v7l7 7 7-7-7-7z"/>
                              <circle cx="5" cy="5" r="1" fill="currentColor" stroke="none"/>
                            </svg>
                            Tags
                          </Link>

                          <Link to={`/main/recintos/${clientId}/eventos/${event.id}/providers`} className="card-action-btn" style={{ textDecoration: 'none' }}>
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                              <path d="M2 6l6-4 6 4v8H2V6z"/>
                              <path d="M6 14v-4h4v4"/>
                            </svg>
                            Proveedores
                          </Link>
                          
                          <Link to={`/main/recintos/${clientId}/eventos/${event.id}/codes`} className="card-action-btn" style={{ textDecoration: 'none' }}>
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                              <rect x="2"    y="4" width="3"   height="8" rx="0.5"/>
                              <rect x="7"    y="4" width="1.5" height="8" rx="0.5"/>
                              <rect x="10.5" y="4" width="3"   height="8" rx="0.5"/>
                            </svg>
                            Códigos
                          </Link>

                          <Link to={`/main/recintos/${clientId}/eventos/${event.id}/scans`} className="card-action-btn" style={{ textDecoration: 'none' }}>
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                              <path d="M2 5V3h2M14 5V3h-2M2 11v2h2M14 11v2h-2M1 8h14"/>
                            </svg>
                            Escaneos
                          </Link>

                          <Link to={`/main/recintos/${clientId}/eventos/${event.id}/statistics`} className="card-action-btn" style={{ textDecoration: 'none' }}>
                            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
                              <path d="M2 13l4-5 3 3 4-7"/>
                            </svg>
                            Estadísticas
                          </Link>

                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}

          {isFilterModalOpen && (
              <FilterModal
                  isOpen={isFilterModalOpen}
                  onClose={() => setIsFilterModalOpen(false)}
                  onApplyFilters={handleApplyFilters}
                  onClearFilters={handleClearFilters}
                  providers={providers}
                  currentFilters={filters}
              />
          )}
        </div>

      ) : (
        // Vista modal — formulario de evento
        <div className="form-overlay">
          <Evento
            event={selectedEvent}
            id_client={clientIdNum}
            onSave={handleFormClose}
            onCancel={handleFormClose}
            onDelete={(eventId) => {
              setEvents(events.filter(event => event.id !== eventId));
              handleFormClose();
            }}
          />
        </div>
      )}
    </div>
  );
};

export default Eventos;
